import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectChangeEvent } from '@mui/material';
import { statusCampaign, TABS } from '@/helpers/constants';
import useAuth from '@/hooks/useAuth';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { setHighlightedFields } from '@/redux/HighlightFields/highlightFields.action';
import { getHighlightedFields } from '@/redux/HighlightFields/highlightFields.reducer';
import { getIsVANSubCR } from '@/redux/SubCampaign/subCampaign.reducer';
import { HighlightedFields } from '@/types';

interface UseFieldSelectionProps {
  tab?: string;
  fieldName?: string;
  fieldSecondaryName?: string;
}

interface UseFieldSelectionReturn {
  isFieldSelected: boolean;
  highlightedFields: HighlightedFields;
  isSelectionModeOn: boolean;
  isCreatorView?: boolean;
  handleFieldSelection: (e?: React.ChangeEvent | React.MouseEvent | SelectChangeEvent<{ value: unknown }>) => void;
}

const TABS_KEYS = {
  description: TABS.description.label,
  attachments: TABS.attachments.label,
  classification: TABS.classification.label,
  affectedHardware: TABS.affectedHardware.label,
  affectedSoftware: TABS.affectedSoftware.label,
  recipients: TABS.recipients.label,
  affectedVehicles: TABS.affectedVehicles.label,
  feedback: TABS.feedback.label,
} as const;

const NON_SELECTABLE_FIELDS_TABS = [TABS_KEYS.feedback];

const SECTIONS_TABS = {
  limitationVolumeManualEntry: 'affectedVehicles',
  problems: 'description',
};

const formatTab = (tab: string): string => {
  if (SECTIONS_TABS[tab]) {
    return TABS_KEYS[SECTIONS_TABS[tab]];
  }

  return TABS_KEYS[tab] || tab;
};

export const useFieldSelection = ({
  tab = '',
  fieldName = '',
  fieldSecondaryName = '',
}: UseFieldSelectionProps): UseFieldSelectionReturn => {
  const dispatch = useDispatch();
  const highlightedFields: HighlightedFields = useSelector(getHighlightedFields);
  const isVANSubCR = useSelector(getIsVANSubCR);
  const { creationUser = {}, version = 1, status = '' } = useSelector(getCampaignInfo) || {};
  const authService = useAuth();
  const isCreatorView = creationUser?.code === authService.userCode && version > 1 && status === statusCampaign.IN_CREATION && !isVANSubCR;
  const formattedFieldName = fieldSecondaryName ? `${fieldName}.${fieldSecondaryName}` : fieldName;
  const formattedTab = tab && formatTab(tab);
  const isSelectionModeOn = useMemo(
    () =>
      !isVANSubCR
      && highlightedFields?.isSelectionMode
      && !!formattedTab
      && !NON_SELECTABLE_FIELDS_TABS.includes(formattedTab),
    [isVANSubCR, highlightedFields?.isSelectionMode, formattedTab],
  );
  const isFieldSelected = useMemo(() => highlightedFields?.selectedFields?.[formattedTab]?.includes(formattedFieldName) && (isCreatorView || isSelectionModeOn),
    [highlightedFields?.selectedFields, formattedTab, formattedFieldName, isCreatorView, isSelectionModeOn]);

  const updateSelectedFields = useCallback((highlightedFieldsInfo: HighlightedFields, tab: string, fieldName: string) => {
    const { isSelectionMode, selectedFields = {} } = highlightedFieldsInfo;

    if ((!isSelectionMode && !isCreatorView) || !tab || !fieldName) return selectedFields;

    const fieldAlreadySelected = selectedFields?.[tab]?.includes(fieldName);

    if (fieldAlreadySelected) {
      return {
        ...selectedFields,
        [tab]: selectedFields?.[tab]?.filter((field) => field !== fieldName),
      };
    }

    return {
      ...selectedFields,
      [tab]: [...(selectedFields?.[tab] || []), fieldName],
    };
  }, [isCreatorView]);

  const handleFieldSelection = useCallback((e?: React.ChangeEvent | React.MouseEvent | SelectChangeEvent<{ value: unknown }>) => {
    if ((!isSelectionModeOn && !isCreatorView) || !formattedTab) return;

    e?.stopPropagation();

    const updatedSelectedFields = updateSelectedFields(highlightedFields, formattedTab, formattedFieldName);

    dispatch(setHighlightedFields({
      ...highlightedFields,
      selectedFields: updatedSelectedFields,
    }));
  },
  [highlightedFields, formattedTab, formattedFieldName, dispatch, updateSelectedFields, isSelectionModeOn, isCreatorView],
  );

  return {
    isFieldSelected,
    highlightedFields,
    isSelectionModeOn,
    isCreatorView,
    handleFieldSelection,
  };
};
